import { Box, Stack, Typography, Switch, FormControlLabel, Divider, Button, TextField } from '@mui/material'
import { alpha } from '@mui/material/styles'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Trans, t } from '@lingui/macro'
import { AIOutputConfig, FormTemplate } from '@/core/models/form.model'

type FillablePdf = FormTemplate['fillablePdf']

interface AiPdfConfigSectionProps {
  aiOutputConfig: AIOutputConfig
  onChange: (config: AIOutputConfig) => void
  fillablePdf?: FillablePdf
  onConfigureWorkflow: () => void
  onPreviewPdf: (file: NonNullable<FillablePdf>) => void
}

export default function AiPdfConfigSection({ aiOutputConfig, onChange, fillablePdf, onConfigureWorkflow, onPreviewPdf }: AiPdfConfigSectionProps) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2.5, sm: 3 },
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
        background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ p: 1.25, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, display: 'inline-flex' }}>
          <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            <Trans>AI PDF Generation</Trans>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
            <Trans>Automatically generate filled PDF documents using AI based on form submissions.</Trans>
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{ mt: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'background.paper', borderRadius: 2, px: 2, py: 0.5 }}
      >
        <FormControlLabel
          sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
          control={<Switch checked={aiOutputConfig.enabled} onChange={(e) => onChange({ ...aiOutputConfig, enabled: e.target.checked })} />}
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              <Trans>Enable AI PDF Generation</Trans>
            </Typography>
          }
          labelPlacement="start"
        />
      </Box>

      {aiOutputConfig.enabled && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label={t`Output Filename Template`}
              value={aiOutputConfig.outputFilenameTemplate || ''}
              onChange={(e) => onChange({ ...aiOutputConfig, outputFilenameTemplate: e.target.value })}
              helperText={
                <Trans>
                  Use placeholders like {'{patientName}'}, {'{date}'} to customize the filename.
                </Trans>
              }
              size="small"
            />
            <TextField
              fullWidth
              label={t`AI Prompt / Instructions`}
              value={aiOutputConfig.prompt || ''}
              onChange={(e) => onChange({ ...aiOutputConfig, prompt: e.target.value })}
              multiline
              minRows={6}
              maxRows={15}
              helperText={
                <Trans>
                  Provide detailed instructions to the AI on how to map form responses to PDF fields. Be specific about formatting, tone, and content
                  expectations.
                </Trans>
              }
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }}
            />

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <Trans>PDF Template Configuration</Trans>
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={onConfigureWorkflow}
                  startIcon={
                    <Box component="span" sx={{ fontSize: 18 }}>
                      🪄
                    </Box>
                  }
                >
                  <Trans>Configure PDF Workflow</Trans>
                </Button>
                {fillablePdf && (
                  <Button variant="outlined" color="success" onClick={() => onPreviewPdf(fillablePdf)} size="small">
                    <Trans>View Burned PDF</Trans>
                  </Button>
                )}
              </Stack>
              {fillablePdf && (
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                  ✓ <Trans>Fillable PDF configured</Trans>: {fillablePdf.filename}
                </Typography>
              )}
              {!fillablePdf && (
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'warning.main' }}>
                  ⚠ <Trans>No fillable PDF configured. Use the workflow to upload and configure a PDF template.</Trans>
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  )
}
